import { useState } from "react";

import {
  normalizeInfinitePaper,
  normalizePaper,
  normalizeSpacing,
  paperBackground,
} from "@/components/reader/paper";
import { CATEGORY_COLOR } from "@/lib/palette";
import { cn } from "@/lib/utils";
import type { NoteCardOut, NoteSurfaceOut } from "@/lib/api";

/** Room at the top of a preview for the folder breadcrumb the card lays over
 * it. Without it the first line of a text excerpt collides with the path. */
const BREADCRUMB_GUTTER = "pt-[26px]";

/** One paper cell in the plate, in px.
 *
 * A canvas page is 1700×2200 virtual units with a 56-unit cell, and the plate
 * shows roughly the top fifth of a page at ~380px wide — which lands a cell at
 * about 13px. Tuned rather than computed: the plate's real width isn't known
 * until layout, and a preview only has to read as "ruled paper", not survive a
 * measurement. */
const PLATE_CELL = 13;

/** mime/category → the short label a plate shows. Client-side on purpose: it is
 * presentation, and the server already ships the two facts it derives from. */
function typeBadge(card: NoteCardOut): string {
  const mime = (card.mime ?? "").split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (card.category === "handwritten") return "Handwritten";
  if (card.category === "website") return "Website";
  if (mime.includes("pdf")) return "PDF";
  if (mime.includes("wordprocessingml")) return "DOCX";
  if (mime.includes("presentationml")) return "PPTX";
  if (mime.includes("spreadsheetml")) return "XLSX";
  if (mime.startsWith("image/")) return "Image";
  if (mime.startsWith("text/")) return "Text";
  const ext = card.title.includes(".") ? card.title.split(".").pop() : "";
  return ext && ext.length <= 4 ? ext.toUpperCase() : "Document";
}

/** Perceived lightness of a hex colour, 0–1. Rec. 601 luma, which is the cheap
 * standard for "will dark text read on this". Unparseable input reads as dark,
 * so an unexpected value falls back to the app's own (dark) treatment rather
 * than putting dark text on a dark plate. */
export function isLightSurface(hex: string | null | undefined): boolean {
  const value = (hex ?? "").trim().replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return (0.299 * r! + 0.587 * g! + 0.114 * b!) / 255 > 0.55;
}

/**
 * A note's preview — the top half of a Leaf card, and the thing that makes the
 * card recognisable at a glance.
 *
 * The mode is chosen by the SERVER so the client never guesses from a mime
 * type, and it degrades in a deliberate order — captured raster, then text
 * excerpt, then the document's own blank page — so a card is never an empty
 * frame and never a generic placeholder.
 *
 * Only the `text` mode is masked. A page-1 raster is the TOP of a document — it
 * has nothing above it — so fading its upper edge would imply content that
 * isn't there. (The brief asked for the fade "where visually appropriate"; this
 * is where it isn't.)
 */
export function NotePreview({ card }: { card: NoteCardOut }) {
  // Every category has an accent, but the map is indexed by string, so TS can't
  // prove it — fall back rather than letting `undefined` reach a style value.
  const accent = CATEGORY_COLOR[card.category] ?? "hsl(var(--muted-foreground))";
  const { mode, text, url, surface } = card.preview;

  if (mode === "text") {
    const body = (text ?? "").trim();
    if (body) {
      return (
        <div className="h-full w-full overflow-hidden bg-muted/50">
          <p
            className={cn(
              "px-3.5 text-[11px] leading-[1.62] text-foreground/70",
              BREADCRUMB_GUTTER,
            )}
            style={{
              // A real mask, not an opaque overlay: the text dissolves into
              // whatever is behind it, so the effect survives the card's hover
              // state and any future surface colour.
              maskImage: "linear-gradient(to bottom, #000 52%, transparent 96%)",
              WebkitMaskImage: "linear-gradient(to bottom, #000 52%, transparent 96%)",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {body}
          </p>
        </div>
      );
    }
  }

  if (mode === "image" && url) {
    return <PreviewImage url={url} accent={accent} surface={surface} />;
  }

  // Nothing captured yet. NOT the server's generated SVG card: that is a light
  // plate with its own hue, its own type badge and its own title, built for
  // contexts with no surrounding chrome. Dropped into this grid it fought the
  // obsidian palette and printed a second title and type label directly under
  // the card's own. The endpoint stays for other consumers; here the studio
  // draws the document's ACTUAL page.
  const badge = typeBadge(card);
  return (
    <PreviewPlate
      accent={accent}
      surface={surface}
      // The breadcrumb directly above already names the category, so a badge
      // that repeats it ("Handwritten" over "Handwritten") is noise.
      badge={badge.toLowerCase() === card.category ? null : badge}
    />
  );
}

function PreviewImage({
  url,
  accent,
  surface,
}: {
  url: string;
  accent: string;
  surface?: NoteSurfaceOut | null;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return <PreviewPlate accent={accent} surface={surface} badge="Preview" />;
  return (
    <div
      className="h-full w-full overflow-hidden bg-muted/50"
      // A raster of a page rarely fills the frame exactly; letting the page's
      // own colour sit behind it keeps the letterboxed edges part of the page
      // instead of a dark seam around it.
      style={surface ? { background: surface.background } : undefined}
    >
      <img
        src={url}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        // A captured raster is a PAGE: anchor it to the top and crop the
        // bottom, so the preview shows the beginning of the document.
        className="h-full w-full object-cover object-top"
      />
    </div>
  );
}

/**
 * The blank page: the document's real background colour, with a canvas note's
 * real ruling drawn on it by the SAME `paperBackground` the editor uses.
 *
 * So a cream ruled notebook previews as cream ruled paper, a grid canvas as
 * grid, and a PDF or captured website as a white page — each recognisable as
 * itself before any raster has ever been captured. Falls back to a wash of the
 * category accent for notes that aren't pages at all.
 */
function PreviewPlate({
  accent,
  badge,
  surface,
}: {
  accent: string;
  badge: string | null;
  surface?: NoteSurfaceOut | null;
}) {
  const light = isLightSurface(surface?.background);
  const ruling = surface?.paper !== undefined ? rulingFor(surface) : null;
  return (
    <div
      className={cn("flex h-full w-full items-end px-3.5 pb-3", BREADCRUMB_GUTTER)}
      style={
        surface
          ? {
              backgroundColor: surface.background,
              ...(ruling ?? {}),
              // A light page against a dark card needs an edge, or it reads as
              // a hole punched in the surface.
              boxShadow: light ? "inset 0 -1px 0 0 rgba(0,0,0,0.16)" : undefined,
            }
          : {
              background: `radial-gradient(120% 90% at 12% 0%, ${accent}26, transparent 68%), hsl(var(--muted) / 0.5)`,
            }
      }
    >
      {badge && (
        <span
          className={cn(
            "font-mono text-[9.5px] uppercase tracking-[0.18em]",
            light ? "text-black/40" : "text-muted-foreground",
          )}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

/** The canvas ruling as CSS, resolved through the editor's own defaulting rules
 * (a missing paper is blank on a paged canvas, dots on an infinite one) so the
 * preview and the editor can never disagree about what a note looks like. */
function rulingFor(surface: NoteSurfaceOut | null | undefined) {
  if (!surface?.paper && !surface?.layout) return null;
  const style =
    surface.layout === "infinite"
      ? normalizeInfinitePaper(surface.paper)
      : normalizePaper(surface.paper);
  return paperBackground(style, PLATE_CELL * normalizeSpacing(surface.spacing)) ?? null;
}
